import { definePolicy, type Policy } from '../kernel';
import { allow, deny } from '../types';

/**
 * SALES ORDER TRANSITION READINESS — the state the order's rows must be in before a lifecycle
 * step may run, beside the status windows of `salesOrderPolicy` (which decide WHETHER the step is
 * offered at all). Sentences are the api's, verbatim.
 *
 *   mark as shipped ─▶ every picked product has its lot, and no lot sits on an unpicked row
 *   revert (SHIPPED) ─▶ no bill on the order has been paid
 *   close           ─▶ every linked (fulfilling) order has shipped — web-only until the api's
 *                      log-only phase (phase 8, D2), so the sentence is the browser's
 *
 * The row predicate is SHARED (#7630): the api's ship validation and the Pick Ticket's
 * Confirm Shipped read the same rows the same way.
 */

/** One live Sales Order product row, as the ship check reads it. */
export type SalesOrderShipReadinessLine = {
  id: number;
  parentLineItemId: number | null;
  external: boolean;
  lotId: number | null;
  unitsPicked: number | null;
};

/** What is still missing before the order can ship, or `null` when every picked row is complete. */
export type SalesOrderPickGap = 'lots-missing' | 'lots-on-unpicked-rows';

/**
 *   picked rows      = source rows + external rows
 *   lots-missing     = a parent with nothing picked and no picked row of its own,
 *                      OR a picked row that shipped units with no lot
 *   lots-on-unpicked = a picked row that holds a lot but picked no units
 */
export const findSalesOrderPickGap = (lines: readonly SalesOrderShipReadinessLine[]): SalesOrderPickGap | null => {
  const picked = lines.filter((line) => line.parentLineItemId !== null || line.external);
  const requiresLot = (line: SalesOrderShipReadinessLine) => (line.unitsPicked ?? 0) > 0 && !line.external;

  const parentWithoutPickedRow = lines.some(
    (line) =>
      line.parentLineItemId === null &&
      !line.unitsPicked &&
      !line.external &&
      !picked.some((row) => row.parentLineItemId === line.id)
  );
  if (parentWithoutPickedRow) return 'lots-missing';

  if (picked.some((line) => line.lotId === null && requiresLot(line))) return 'lots-missing';

  if (picked.some((line) => line.lotId !== null && !line.unitsPicked && !line.external)) {
    return 'lots-on-unpicked-rows';
  }

  return null;
};

export type SalesOrderShipReadinessFacts = { pickGap: SalesOrderPickGap | null };

export const salesOrderShipReadinessPolicy: Policy<SalesOrderShipReadinessFacts, never, 'mark as shipped'> =
  definePolicy<SalesOrderShipReadinessFacts, never, 'mark as shipped'>({
    kind: 'sales order shipment readiness',
    fields: [],
    ops: ['mark as shipped'],
    requires: {},
    entity: (facts, action) => {
      if (action.type !== 'perform') return allow();

      switch (facts.pickGap) {
        case 'lots-missing':
          return deny('SALES_ORDER_SHIP_LOTS_MISSING', 'Add a lot to every picked product to ship.');
        case 'lots-on-unpicked-rows':
          return deny(
            'SALES_ORDER_SHIP_LOTS_ON_UNPICKED_ROWS',
            'Remove lots from unpicked products, or enter units delivered.'
          );
        case null:
          return allow();
      }
    },
  });

export type SalesOrderRevertReadinessFacts = {
  /** A live bill on the order is PAID_PARTIAL, PAID_FULL or FINALIZED. */
  hasPaidBills: boolean;
};

export const salesOrderRevertReadinessPolicy: Policy<SalesOrderRevertReadinessFacts, never, 'revert'> = definePolicy<
  SalesOrderRevertReadinessFacts,
  never,
  'revert'
>({
  kind: 'sales order revert readiness',
  fields: [],
  ops: ['revert'],
  requires: {},
  entity: (facts, action) =>
    action.type === 'perform' && facts.hasPaidBills
      ? deny('SALES_ORDER_REVERT_BILL_PAID', "Can't revert: a bill on this sales order is paid.")
      : allow(),
});

/**
 * A linked order is DONE for Close once it shipped (SHIPPED, CLOSED) or was CANCELLED: a cancelled
 * order will never ship, so it must not hold its sales order open (TAS-3660 P8 fix — prod closed 7
 * sales orders whose only unshipped linked orders were CANCELLED).
 */
const DONE_LINKED_ORDER_STATUSES: readonly string[] = ['SHIPPED', 'CLOSED', 'CANCELLED'];

/** Shared fact predicate: every linked order is done (shipped, closed or cancelled). None linked ⇒ true. */
export const allLinkedOrdersShipped = (linkedOrderStatuses: readonly string[]): boolean =>
  linkedOrderStatuses.every((status) => DONE_LINKED_ORDER_STATUSES.includes(status));

export type SalesOrderCloseReadinessFacts = { allLinkedOrdersShipped: boolean };

export const salesOrderCloseReadinessPolicy: Policy<SalesOrderCloseReadinessFacts, never, 'close'> = definePolicy<
  SalesOrderCloseReadinessFacts,
  never,
  'close'
>({
  kind: 'sales order close readiness',
  fields: [],
  ops: ['close'],
  requires: {},
  entity: (facts, action) =>
    action.type === 'perform' && !facts.allLinkedOrdersShipped
      ? deny('SALES_ORDER_CLOSE_LINKED_ORDERS_OPEN', 'Ship every linked order before closing.')
      : allow(),
});
